import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Landmark, Check, X, FileSpreadsheet, Sparkles } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, date } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, Checkbox, EmptyState,
  LoadingBlock, Table, TableWrap, Td, Th, useToast,
} from './ui';
import type { BankTransaction, BankImportResult } from '@shared/types';

/**
 * Reconciling a month of rent against a bank statement.
 *
 * ── Why this is allowed near the ledger ───────────────────────────────────
 *
 * Everywhere else, a payment counts when a person says it arrived, and a
 * tenant's claim never moves a balance by itself. This is not an exception to
 * that, because of whose evidence it is: a statement is the landlord's own
 * bank telling them money arrived — the same fact they would confirm by hand,
 * from the same screen, in the same five minutes.
 *
 * What the screen has to earn is trust in the matching, which is why every
 * suggestion says *why* it was matched rather than presenting a number and
 * asking to be believed.
 */
export function BankImport() {
  const { currency, can } = useAuth();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BankImportResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [showConfirmed, setShowConfirmed] = useState(false);

  /*
   * Two different questions, so two different requests: "what is waiting on
   * me" and "what has ever been imported". Filtering one fetched page in the
   * browser would mean a long history could push the unmatched credits — the
   * ones that need a person — off the end of it.
   */
  const { data, loading, reload } = useApi<{
    transactions: BankTransaction[];
    counts: Record<string, number>;
    truncated: boolean;
    total_matching: number;
  }>('/banking/transactions', showConfirmed ? {} : { status: 'suggested' });

  const { data: strays } = useApi<{ transactions: BankTransaction[] }>(
    showConfirmed ? null : '/banking/transactions', { status: 'unmatched' },
  );

  const all = data?.transactions ?? [];
  const rows = showConfirmed ? all : all.concat(strays?.transactions ?? []);

  const suggestions = rows.filter((t) => t.status === 'suggested');
  const selectable = suggestions.map((t) => t.id);
  const allSelected = selectable.length > 0 && selectable.every((id) => selected.has(id));

  async function upload(file: File) {
    setImporting(true);
    setResult(null);
    try {
      const csv = await file.text();
      const res = await api.post<BankImportResult>('/banking/import', {
        csv,
        label: file.name.replace(/\.[^.]+$/, ''),
      });
      setResult(res);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That file could not be read');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function confirm(ids: string[]) {
    if (ids.length === 0) return;
    setConfirming(true);
    try {
      const res = await api.post<{ confirmed: number; skipped: number }>(
        '/banking/confirm', { transaction_ids: ids },
      );
      toast.success(res.skipped > 0
        ? `${res.confirmed} recorded, ${res.skipped} skipped`
        : `${res.confirmed} payment${res.confirmed === 1 ? '' : 's'} recorded`);
      setSelected(new Set());
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Those could not be confirmed');
    } finally {
      setConfirming(false);
    }
  }

  async function ignore(row: BankTransaction) {
    try {
      await api.post(`/banking/transactions/${row.id}/ignore`, {});
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be set aside');
    }
  }

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="shrink-0 h-10 w-10 rounded-xl bg-brand/12 flex items-center justify-center">
            <Landmark className="h-5 w-5 text-brand" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <h3 className="font-semibold">Import a bank statement</h3>
            <p className="text-sm text-muted leading-relaxed">
              Export a CSV from your online banking and drop it here. Credits are matched
              to what is outstanding, mostly on the reference each tenant was asked to
              quote. Money paid out is ignored and never stored.
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
          <Button
            variant="primary"
            icon={<Upload className="h-4 w-4" />}
            loading={importing}
            disabled={!can('manager')}
            onClick={() => fileRef.current?.click()}
          >
            Choose a file
          </Button>
        </div>

        {result && (
          <div className="mt-4 pt-4 border-t border-line space-y-1 text-sm">
            <p className="text-ink font-medium">
              Read {result.read} credit{result.read === 1 ? '' : 's'} from {result.label}
            </p>
            <ul className="text-muted space-y-0.5">
              {result.confirmed > 0 && (
                <li>{result.confirmed} recorded automatically — reference and amount both exact</li>
              )}
              {result.suggested > 0 && <li>{result.suggested} matched, waiting for you below</li>}
              {result.unmatched > 0 && (
                <li>{result.unmatched} could not be matched to anything outstanding</li>
              )}
              {/* Expected rather than a problem: statements overlap. */}
              {result.duplicates > 0 && (
                <li>{result.duplicates} were already imported and have been left alone</li>
              )}
              {result.unreadable_rows > 0 && (
                <li>{result.unreadable_rows} rows had no date or amount we could read</li>
              )}
            </ul>
          </div>
        )}
      </Card>

      {loading && !data ? <LoadingBlock rows={5} /> : rows.length === 0 ? (
        <EmptyState
          icon={<FileSpreadsheet className="h-6 w-6" />}
          title={showConfirmed ? 'Nothing imported yet' : 'Nothing waiting'}
          message="Credits from an imported statement appear here, matched to what is outstanding."
          action={
            all.length > 0 ? (
              <Button onClick={() => setShowConfirmed(true)}>Show everything imported</Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="p-0 overflow-hidden">
          <CardHeader
            title="Credits from your statement"
            subtitle={data
              ? `${data.counts.suggested} matched · ${data.counts.unmatched} unmatched`
              : undefined}
            action={
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => setShowConfirmed((v) => !v)}>
                  {showConfirmed ? 'Hide settled' : 'Show everything'}
                </Button>
                {can('manager') && selectable.length > 0 && (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={confirming}
                    onClick={() => void confirm(
                      selected.size > 0 ? Array.from(selected) : selectable,
                    )}
                  >
                    {selected.size > 0
                      ? `Record ${selected.size}`
                      : `Record all ${selectable.length}`}
                  </Button>
                )}
              </div>
            }
          />

          {data?.truncated && (
            <p className="px-4 sm:px-5 py-2 text-xs text-faint border-b border-line">
              Showing {rows.length} of {data.total_matching}. Record or set aside some
              to see the rest.
            </p>
          )}

          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onChange={() => setSelected(
                        allSelected ? new Set() : new Set(selectable),
                      )}
                      aria-label="Select every matched credit"
                    />
                  </Th>
                  <Th>Paid in</Th>
                  <Th>What the bank called it</Th>
                  <Th>Matched to</Th>
                  <Th align="right">Amount</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-line">
                    <Td>
                      {row.status === 'suggested' && (
                        <Checkbox
                          checked={selected.has(row.id)}
                          onChange={() => setSelected((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
                            return next;
                          })}
                          aria-label={`Select the credit of ${money(row.amount_cents, currency)}`}
                        />
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-muted">{date(row.posted_on)}</Td>
                    <Td>
                      <p className="text-sm truncate max-w-[280px]">{row.description}</p>
                      {row.reference && (
                        <p className="text-xs text-faint font-mono truncate max-w-[280px]">
                          {row.reference}
                        </p>
                      )}
                    </Td>
                    <Td>
                      {row.tenant_id ? (
                        <>
                          <Link
                            to={`/app/tenants/${row.tenant_id}`}
                            className="text-sm hover:text-brand"
                          >
                            {row.tenant_name}
                          </Link>
                          {/*
                            Why, not just who. A suggestion that cannot explain
                            itself is one somebody ticks without reading.
                          */}
                          <p className="text-xs text-faint">{row.match_reason}</p>
                        </>
                      ) : (
                        <span className="text-sm text-muted">
                          Nothing outstanding looks like this
                        </span>
                      )}
                    </Td>
                    <Td align="right" className="tnum font-semibold whitespace-nowrap">
                      {money(row.amount_cents, currency)}
                    </Td>
                    <Td>
                      <div className="flex items-center justify-end gap-1.5">
                        {row.status === 'confirmed' ? (
                          <Badge tone="ok">Recorded</Badge>
                        ) : row.status === 'ignored' ? (
                          <Badge tone="neutral">Set aside</Badge>
                        ) : can('manager') ? (
                          <>
                            {row.status === 'suggested' && (
                              <Button
                                size="sm"
                                variant="primary"
                                icon={<Check className="h-3.5 w-3.5" />}
                                onClick={() => void confirm([row.id])}
                              >
                                Record
                              </Button>
                            )}
                            <Button
                              size="sm"
                              icon={<X className="h-3.5 w-3.5" />}
                              onClick={() => void ignore(row)}
                            >
                              Not rent
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      <AutoConfirmNote />
    </div>
  );
}

/** Explains the one setting that lets this skip the review step. */
function AutoConfirmNote() {
  const { staff } = useAuth();
  if (staff?.role !== 'owner') return null;

  return (
    <div className="flex items-start gap-3 p-4 rounded-2xl bg-raised border border-line">
      <Sparkles className="h-5 w-5 text-faint shrink-0 mt-0.5" />
      <p className="text-sm text-muted leading-relaxed">
        Credits where the reference <em>and</em> the amount both match exactly can be
        recorded without this step. It is off until you turn it on, in{' '}
        <Link to="/app/settings" className="text-brand hover:underline">Settings</Link> —
        worth doing once you have watched it get a month right. A short payment is never
        recorded on its own, whatever the setting says: deciding what one means is a
        judgement about a tenancy rather than a sum.
      </p>
    </div>
  );
}
